import React, { useEffect, useState } from "react";
import TagInput from "../../../common/TagInput";
import { fournisseurService } from "../../../../services/fournisseurService";
import { financierService } from "../../../../services/financierService";
import styles from "./EnvoyerAuFinanceurModal.module.scss";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * LS-625 — un seul flux pour l'envoi initial et le renvoi. Préremplit
 * destinataire/cc/fournisseur/commentaire avec le dernier envoi (dernierEnvoi,
 * dénormalisé côté API) si la demande a déjà été envoyée, sinon avec l'email
 * par défaut du financeur. La preview du corps du mail (template ST1-1995)
 * est calquée côté front, sans appel serveur dédié.
 *
 * UX : champs d'envoi à gauche, aperçu façon vrai mail à droite ; le
 * commentaire se saisit directement dans le corps du mail, à l'endroit
 * où il apparaîtra.
 */
const EnvoyerAuFinanceurModal = ({
  isOpen,
  financeurUuid,
  dernierEnvoi,
  expediteurSignatureUrl,
  onSubmit,
  onClose,
}) => {
  const [destinataire, setDestinataire] = useState("");
  const [cc, setCc] = useState([]);
  const [fournisseurUuid, setFournisseurUuid] = useState("");
  const [commentaire, setCommentaire] = useState("");
  const [fournisseurs, setFournisseurs] = useState([]);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setIsPreparing(true);

    async function prepare() {
      let list = [];
      try {
        const res = await fournisseurService.getAllFournisseurs();
        list = Array.isArray(res.data) ? res.data : res.data?.data || [];
      } catch {
        list = [];
      }
      if (cancelled) return;
      setFournisseurs(list);

      if (dernierEnvoi) {
        setDestinataire(dernierEnvoi.destinataire || "");
        setCc(dernierEnvoi.cc || []);
        setCommentaire(dernierEnvoi.commentaire || "");
        // Le fournisseur n'est stocké qu'en snapshot nom/description sur la
        // demande (pas d'uuid) : on tente de resélectionner par nom dans le
        // référentiel courant, sinon la preview retombe sur le snapshot figé.
        const match = list.find((f) => f.nom === dernierEnvoi.fournisseurNom);
        setFournisseurUuid(match ? match.uuid : "");
      } else {
        setCc([]);
        setCommentaire("");
        setFournisseurUuid("");
        if (financeurUuid) {
          try {
            const res = await financierService.getFinancier(financeurUuid);
            if (!cancelled) {
              setDestinataire(res.data?.email || "");
              setCc(res.data?.emailsCc || []);
            }
          } catch {
            if (!cancelled) setDestinataire("");
          }
        } else {
          setDestinataire("");
        }
      }
      if (!cancelled) setIsPreparing(false);
    }

    prepare();
    return () => {
      cancelled = true;
    };
  }, [isOpen, financeurUuid, dernierEnvoi]);

  if (!isOpen) return null;

  const selectedFournisseur = fournisseurs.find(
    (f) => f.uuid === fournisseurUuid,
  );
  const previewFournisseur =
    selectedFournisseur ||
    (dernierEnvoi && !fournisseurUuid
      ? {
          nom: dernierEnvoi.fournisseurNom,
          description: dernierEnvoi.fournisseurDescription,
        }
      : null);

  const emailOk =
    destinataire.trim() !== "" && EMAIL_REGEX.test(destinataire.trim());
  const emailInvalide = destinataire.trim() !== "" && !emailOk;
  const isValid = emailOk && fournisseurUuid !== "";

  const busy = isSubmitting;

  const handleCancel = () => {
    if (busy) return;
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") handleCancel();
  };

  const handleSubmit = async () => {
    if (!isValid || busy) return;
    setIsSubmitting(true);
    const ok = await onSubmit({
      destinataire: destinataire.trim(),
      cc,
      fournisseurId: fournisseurUuid,
      commentaire: commentaire.trim() || null,
    });
    setIsSubmitting(false);
    if (ok) onClose();
  };

  const manque = [];
  if (!emailOk) manque.push("un destinataire valide");
  if (!fournisseurUuid) manque.push("un fournisseur");

  return (
    <div
      className={`modal d-block ${styles.backdrop}`}
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="envoyer-financeur-title"
      onClick={handleCancel}
      onKeyDown={handleKeyDown}
    >
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`modal-content ${styles.content}`}>
          <div className={styles.header}>
            <div className={styles.headerIcon} aria-hidden="true">
              <i className="fa fa-paper-plane" />
            </div>
            <div className={styles.headerText}>
              <h5 className="modal-title" id="envoyer-financeur-title">
                {dernierEnvoi ? "Renvoyer" : "Envoyer"} la demande de
                financement au financeur
              </h5>
              {dernierEnvoi && (
                <small>
                  Les champs reprennent le dernier envoi — modifiez-les si
                  besoin.
                </small>
              )}
            </div>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={handleCancel}
              aria-label="Fermer"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div className={styles.body}>
            {isPreparing ? (
              <div className={styles.loading}>
                <i className="fa fa-spinner fa-spin" aria-hidden="true" />
                Préparation de l'envoi…
              </div>
            ) : (
              <>
                <div className={styles.fieldsCol}>
                  <div
                    className={`${styles.field} ${
                      emailInvalide ? styles.fieldError : ""
                    }`}
                  >
                    <label htmlFor="envoyer-financeur-destinataire">
                      Destinataire
                    </label>
                    <input
                      id="envoyer-financeur-destinataire"
                      type="email"
                      className="form-control"
                      value={destinataire}
                      onChange={(e) => setDestinataire(e.target.value)}
                      placeholder="contact@financeur.fr"
                      disabled={busy}
                      autoFocus
                    />
                    {emailInvalide && (
                      <p className={styles.errorText}>
                        Cette adresse email n'est pas valide.
                      </p>
                    )}
                  </div>

                  <TagInput
                    name="cc"
                    label="Copie (Cc)"
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                    placeholder="Ajouter un email en copie..."
                    className={styles.ccField}
                  />

                  <div className={styles.field}>
                    <label htmlFor="envoyer-financeur-fournisseur">
                      Fournisseur
                    </label>
                    <select
                      id="envoyer-financeur-fournisseur"
                      className="form-control"
                      value={fournisseurUuid}
                      onChange={(e) => setFournisseurUuid(e.target.value)}
                      disabled={busy}
                    >
                      <option value="">— Sélectionner un fournisseur —</option>
                      {fournisseurs.map((f) => (
                        <option key={f.uuid} value={f.uuid}>
                          {f.nom}
                        </option>
                      ))}
                    </select>
                    <p className={styles.hintText}>
                      Ses coordonnées sont insérées dans le mail.
                    </p>
                    {dernierEnvoi &&
                      !selectedFournisseur &&
                      dernierEnvoi.fournisseurNom && (
                        <p className={styles.errorText}>
                          Dernier envoi : {dernierEnvoi.fournisseurNom}{" "}
                          (introuvable dans le référentiel — sélectionnez-en
                          un nouveau)
                        </p>
                      )}
                  </div>
                </div>

                <div className={styles.previewCol}>
                  <p className={styles.colLabel}>Aperçu du mail</p>
                  <div className={styles.mailCard}>
                    <div className={styles.mailAttachRow}>
                      <span className={styles.attachChip}>
                        <i className="fa fa-paperclip" aria-hidden="true" />
                        Tableau récapitulatif
                      </span>
                      <p className={styles.attachNote}>
                        Le tableau récapitulatif à jour sera automatiquement
                        joint au mail.
                      </p>
                    </div>
                    <div className={styles.mailBody}>
                      <p>Bonjour,</p>
                      <p>
                        Pourriez-vous nous faire parvenir l'accord de
                        financement pour la demande en pièce jointe svp ?
                      </p>
                      <div
                        className={`${styles.mailFournisseur} ${
                          previewFournisseur ? "" : styles.mailFournisseurEmpty
                        }`}
                      >
                        <strong>FOURNISSEUR</strong>
                        {previewFournisseur ? (
                          <>
                            <span>{previewFournisseur.nom}</span>
                            {previewFournisseur.description && (
                              <span>{previewFournisseur.description}</span>
                            )}
                          </>
                        ) : (
                          <span>Aucun fournisseur sélectionné</span>
                        )}
                      </div>
                      <textarea
                        className={styles.commentInput}
                        rows={2}
                        value={commentaire}
                        onChange={(e) => setCommentaire(e.target.value)}
                        disabled={busy}
                        placeholder="Ajouter un commentaire dans le mail (optionnel)…"
                        aria-label="Commentaire ajouté au mail"
                      />
                      <p>En vous remerciant par avance.</p>
                      <p>Vous pouvez répondre directement à ce message.</p>
                      <p style={{ marginBottom: expediteurSignatureUrl ? 4 : 0 }}>
                        Bien cordialement
                      </p>
                      {expediteurSignatureUrl && (
                        <p style={{ marginBottom: 0 }}>
                          <img
                            src={expediteurSignatureUrl}
                            alt="signature"
                            style={{ maxHeight: 60 }}
                          />
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className={styles.footer}>
            <p className={styles.footerHint}>
              {!isPreparing && manque.length > 0
                ? `Pour envoyer, renseignez ${manque.join(" et ")}.`
                : ""}
            </p>
            <button
              type="button"
              className={styles.btnCancel}
              onClick={handleCancel}
              disabled={busy}
            >
              Annuler
            </button>
            <button
              type="button"
              className={styles.btnSend}
              onClick={handleSubmit}
              disabled={busy || !isValid || isPreparing}
            >
              <i
                className={`fa fa-${busy ? "spinner fa-spin" : "paper-plane"}`}
                aria-hidden="true"
              />
              {busy ? "Envoi..." : "Envoyer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvoyerAuFinanceurModal;
